import { type FC, useEffect, useState } from 'react';
import { Platform, type ViewStyle } from 'react-native';
import { Picker } from 'react-native-wheel-pick';

import { SelectContent } from '@/components/ui/select/select-content';
import { useSelectContext } from '@/components/ui/select/select-context';
import type { SelectOption } from '@/components/ui/select/types';
import { WheelPickerSheet } from '@/components/ui/wheel-picker-sheet';
import { createStyles } from '@/hooks';
import { colors } from '@/theme/colors';

type WheelPickerProps = {
  style?: ViewStyle | ViewStyle[];
  selectedValue?: string;
  pickerData: SelectOption[];
  onValueChange?: (value: string) => void;
  textColor?: string;
  selectTextColor?: string;
  /** Android: hide the native full-width selection band/lines so our rounded pill shows instead. */
  isShowSelectBackground?: boolean;
  isShowSelectLine?: boolean;
};

const WheelPicker = Picker as unknown as FC<WheelPickerProps>;

export type SelectSheetContentProps = {
  /** Title shown in the sheet header. */
  title?: string;
  /** Min width of the web dropdown fallback (narrow triggers, e.g. a flag). */
  dropdownMinWidth?: number;
  /** Extra style merged into the phone bottom-sheet container (e.g. override corner radius). */
  sheetStyle?: ViewStyle | ViewStyle[];
};

/**
 * Bottom-sheet wheel presentation for `Select` — an alternative to `Select.Content`'s
 * dropdown. Reuses the Select context (so the same `Select.Trigger` opens it), holds a
 * temp value while scrolling, and commits via the header check. The wheel library has no
 * web support, so the web build falls back to the dropdown content.
 */
export const SelectSheetContent: FC<SelectSheetContentProps> = ({
  title,
  dropdownMinWidth,
  sheetStyle,
}) => {
  const { open, setOpen, options, selectedValue, setValue } = useSelectContext();
  const styles = useStyles();
  const [tempValue, setTempValue] = useState<string | null>(selectedValue);

  const [seedValue, setSeedValue] = useState<string | null>(selectedValue);

  useEffect(() => {
    if (!open) return;
    const hasCommitted = options.some(o => o.value === selectedValue);
    const seed = hasCommitted ? selectedValue : (options[0]?.value ?? null);
    setSeedValue(seed);
    setTempValue(seed);
  }, [open, selectedValue, options]);

  if (Platform.OS === 'web') return <SelectContent minWidth={dropdownMinWidth} />;

  const confirm = () => {
    if (tempValue != null) setValue(tempValue);
    setOpen(false);
  };

  return (
    <WheelPickerSheet
      open={open}
      onOpenChange={setOpen}
      title={title}
      onConfirm={confirm}
      sheetStyle={sheetStyle}
      showSelectionPill>
      <WheelPicker
        key={open ? 'open' : 'closed'}
        style={styles.wheel}
        selectedValue={seedValue ?? undefined}
        pickerData={options}
        onValueChange={next => setTempValue(String(next))}
        textColor={colors['10']}
        selectTextColor={colors['10']}
        // Native band off → the sheet's rounded inset pill is the only selection highlight.
        isShowSelectBackground={false}
        isShowSelectLine={false}
      />
    </WheelPickerSheet>
  );
};

const useStyles = createStyles(({ scale }) => ({
  wheel: {
    width: '100%',
    height: scale(220),
    // Transparent so the sheet's rounded selection pill (rendered behind) shows through.
    backgroundColor: 'transparent',
  },
}));

export default SelectSheetContent;
